import React from 'react';
import { useResetPasswordHook } from './useResetPasswordHook';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, XCircle } from 'lucide-react';
import { Input } from '@/shared/components/atoms/Input';
import { Button } from '@/shared/components/atoms/Button';
import { AuthLayout } from '../../components/AuthLayout';
import '../Login/Login.css';

const ResetPasswordLeftContent = () => (
  <>
    <p className="login-tagline">
      Secure your account and return to closing deals.
    </p>

    <div className="login-features">
      <div className="login-feature-item">
        <span className="login-feature-bullet" />
        <p className="login-feature-text">
          <strong>Strong Password:</strong> Use a combination of letters, numbers, and symbols to protect your data.
        </p>
      </div>
    </div>
  </>
);

const ResetPassword = () => {
  const { formData, handleChange, isLoading, handleSubmit, showNewPassword, setShowNewPassword, showConfirmPassword, setShowConfirmPassword, isValidating, validationError } = useResetPasswordHook();

  if (isValidating) {
    return (
      <AuthLayout leftContent={<ResetPasswordLeftContent />}>
        <div className="flex flex-col items-center justify-center space-y-4 py-12">
          <svg className="animate-spin h-8 w-8 text-[#00cfcf]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-400">Validating reset link...</p>
        </div>
      </AuthLayout>
    );
  }

  if (validationError) {
    return (
      <AuthLayout leftContent={<ResetPasswordLeftContent />}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="text-center space-y-6 py-8 flex flex-col items-center"
        >
          <div className="w-16 h-16 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center mb-4 border border-red-500/20">
            <XCircle size={32} />
          </div>
          <div>
            <h2 className="auth-heading">Link Expired or Invalid</h2>
            <p className="text-gray-300 mt-2">
              This password reset link is invalid, has already been used, or has expired.
            </p>
          </div>
          <Link to="/forgot-password">
            <Button variant="secondary" style={{ marginTop: '16px' }}>
              Request New Link
            </Button>
          </Link>
          <div className="mt-8">
            <Link to="/login" className="auth-switch-link-text hover:text-[#00cfcf] transition-colors">
              Return to Login
            </Link>
          </div>
        </motion.div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout leftContent={<ResetPasswordLeftContent />}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <h2 className="auth-heading">Create New Password</h2>
        <p className="auth-subheading">Your new password must be different from previous ones.</p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="auth-input-group" style={{ marginBottom: '12px' }}>
            <Input
              label="New Password"
              leftIcon={<Lock size={13} />}
              type={showNewPassword ? 'text' : 'password'}
              name="newPassword"
              id="new-password"
              placeholder="••••••••"
              value={formData.newPassword}
              onChange={handleChange}
              required
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  className="password-toggle-btn"
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              }
            />
            <Input
              label="Confirm Password"
              leftIcon={<Lock size={13} />}
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              id="confirm-password"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="password-toggle-btn"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              }
            />
          </div>

          <Button type="submit" variant="secondary" fullWidth isLoading={isLoading} style={{ marginTop: '8px' }}>
            Reset Password
          </Button>
        </form>

        <p className="auth-switch-link-text">
          Remember your password?{' '}
          <Link to="/login" className="auth-switch-link">
            Sign In
          </Link>
        </p>
      </motion.div>
    </AuthLayout>
  );
};

export default ResetPassword;
